import { Injectable } from '@angular/core';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AllocationViewProfileItemService } from './allocation-view-profile-item.service';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { AllocationDriverViewProfile, AllocationViewFilterProfileModel, AllocationViewProfile } from '../models';
import {
  AllocationViewFilterItemLoad,
  AllocationViewProfileItemDelete,
  AllocationViewProfileItemDriverViewLoad,
  AllocationViewProfileItemInitialState,
  AllocationViewProfileItemLoad,
  AllocationViewProfileItemLoadDefault,
  AllocationViewProfileItemSave,
  AllocationViewProfileItemSaveWithFilter,
} from './allocation-view-profile-item.actions';
import { CustomHttpErrorResponse, Error } from '../../../../../common';
import { AllocationViewFacade } from '../../allocation-view/allocation-view.facade';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AllocationViewProfileItemStateModel {
  loaded: boolean;
  profile: AllocationViewProfile;
  defaultProfile: AllocationViewProfile;
  defaultProfileLoaded: boolean;
  filterProfile: AllocationViewFilterProfileModel;
  filterProfileLoaded: boolean;
  driverViewProfile: AllocationDriverViewProfile;
  driverViewProfileLoaded: boolean;
}

const initialState: AllocationViewProfileItemStateModel = {
  loaded: false,
  profile: null,
  defaultProfile: null,
  defaultProfileLoaded: false,
  filterProfile: null,
  filterProfileLoaded: false,
  driverViewProfile: null,
  driverViewProfileLoaded: false,
};

@State({
  name: 'allocationViewProfileItem',
  defaults: initialState,
})
@Injectable()
export class AllocationViewProfileItemState {
  constructor(
    private allocationViewFacade: AllocationViewFacade,
    private notificationService: NotificationService,
    private allocationViewProfileItemService: AllocationViewProfileItemService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getLoaded(state: AllocationViewProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getDefaultLoaded(state: AllocationViewProfileItemStateModel): boolean {
    return state.defaultProfileLoaded;
  }

  @Selector()
  static getProfileLoaded(state: AllocationViewProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getProfile(state: AllocationViewProfileItemStateModel): AllocationViewProfile {
    return state.profile;
  }

  @Selector()
  static getProfileId(state: AllocationViewProfileItemStateModel): number {
    return state.profile.id;
  }

  @Selector()
  static getDefaultProfile(state: AllocationViewProfileItemStateModel): AllocationViewProfile {
    return state.defaultProfile;
  }

  @Selector()
  static getFilterProfile(state: AllocationViewProfileItemStateModel): AllocationViewFilterProfileModel {
    return state.filterProfile;
  }

  @Selector()
  static getDriverViewProfile(state: AllocationViewProfileItemStateModel): AllocationDriverViewProfile {
    return state.driverViewProfile;
  }

  @Selector()
  static getDriverViewProfileLoaded(state: AllocationViewProfileItemStateModel): boolean {
    return state.driverViewProfileLoaded;
  }

  @Action(AllocationViewProfileItemLoad)
  loadProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { id }: AllocationViewProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationViewFilterProfileModel | AllocationViewProfile> {
    return this.allocationViewProfileItemService.getProfile(id).pipe(
      tap((profile: AllocationViewProfile) => {
        return ctx.patchState({
          profile,
          loaded: true,
        });
      }),
      map((profile: AllocationViewProfile) => profile.filterProfileId),
      filter(Boolean),
      switchMap((filterId: number) => {
        return this.allocationViewProfileItemService.getFilterProfile(filterId).pipe(
          tap((filterProfile: AllocationViewFilterProfileModel) => {
            ctx.patchState({
              filterProfile,
            });
          }),
        );
      }),

      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewProfileItemDriverViewLoad)
  loadDriverViewProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { id }: AllocationViewProfileItemDriverViewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationViewFilterProfileModel | AllocationDriverViewProfile> {
    return this.allocationViewProfileItemService.getDriverViewProfile(id).pipe(
      tap((driverViewProfile: AllocationDriverViewProfile) => {
        return ctx.patchState({
          driverViewProfile,
          driverViewProfileLoaded: true,
        });
      }),
    );
  }

  @Action(AllocationViewFilterItemLoad)
  loadfilterProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { id }: AllocationViewFilterItemLoad,
  ): Observable<AllocationViewFilterProfileModel> {
    return this.allocationViewProfileItemService.getFilterProfile(id).pipe(
      tap((allocationViewFilterProfileModel: AllocationViewFilterProfileModel) => {
        ctx.patchState({
          filterProfile: allocationViewFilterProfileModel,
          filterProfileLoaded: true,
        });
      }),
    );
  }

  @Action(AllocationViewProfileItemLoadDefault)
  loadDefaultProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationViewFilterProfileModel | AllocationViewProfile> {
    return this.allocationViewProfileItemService.getDefaultProfile().pipe(
      tap((defaultProfile: AllocationViewProfile) => {
        ctx.patchState({
          defaultProfile,
          defaultProfileLoaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewProfileItemSave)
  saveProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { profile }: AllocationViewProfileItemSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationViewProfile> {
    return this.allocationViewProfileItemService.saveProfile(profile).pipe(
      tap((savedProfile: AllocationViewProfile) => {
        ctx.patchState({
          profile: savedProfile,
        });
      }),
      switchMap((newProfile: AllocationViewProfile) => {
        return this.allocationViewFacade.selectedProfile$.pipe(
          filter(
            (selectedProfile: AllocationViewProfile | null) =>
              !!selectedProfile && selectedProfile?.id === newProfile.id,
          ),
          take(1),
          tap(() => {
            this.allocationViewFacade.setProfile(newProfile);
          }),
        );
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewProfileItemSaveWithFilter)
  saveProfileWithFilter(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { filters, profile }: AllocationViewProfileItemSaveWithFilter,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationViewProfile> {
    const isProfileAvailabe = !!profile.id;
    return this.allocationViewProfileItemService
      .createOrUpdateFilterProfile(profile.name, profile.filterProfileId, filters)
      .pipe(
        switchMap((filterProfile: AllocationViewFilterProfileModel) => {
          profile.filterProfileId = filterProfile.id;
          return this.allocationViewProfileItemService.saveProfile(profile).pipe(
            tap((savedProfile: AllocationViewProfile) => {
              ctx.patchState({
                profile: savedProfile,
              });
              if (isProfileAvailabe) {
                this.notificationService.success('api.allocationViewProfile.success.update');
              } else {
                this.notificationService.success('api.allocationViewProfile.success.create');
              }
            }),
            switchMap((newProfile: AllocationViewProfile) => {
              return this.allocationViewFacade.selectedProfile$.pipe(
                filter(
                  (selectedProfile: AllocationViewProfile | null) =>
                    !!selectedProfile && selectedProfile?.id === newProfile.id,
                ),
                take(1),
                tap(() => {
                  this.allocationViewFacade.setProfile(newProfile);
                }),
              );
            }),
            catchError(({ error }: CustomHttpErrorResponse<Error>) =>
              of(this.notificationService.error(error.message)),
            ),
          );
        }),
      );
  }

  @Action(AllocationViewProfileItemDelete)
  deleteProfile(
    ctx: StateContext<AllocationViewProfileItemStateModel>,
    { id, dialogRef }: AllocationViewProfileItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const profileFilter = ctx.getState().filterProfile;
    return this.allocationViewProfileItemService.deleteProfile(id).pipe(
      switchMap(() => {
        if (profileFilter?.id) {
          return this.allocationViewProfileItemService.deleteFilterProfile(profileFilter.id);
        }
        return of(true);
      }),
      map(() => {
        dialogRef.close();
        this.notificationService.success('api.allocationViewProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/allocation-view-profiles');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewProfileItemInitialState)
  setInitialState(ctx: StateContext<AllocationViewProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
